import { useReducer, useMemo } from "react";
import { createInitialState, tableStateReducer } from "./TableStateManager";
import type { SortState } from "./types";

interface UseTableStateConfig {
  defaultPageSize: number;
  defaultSort?: SortState | null;
  initialData?: unknown[];
}

export function useTableState(config: UseTableStateConfig) {
  const [state, dispatch] = useReducer(tableStateReducer, config, (cfg) =>
    createInitialState(cfg),
  );

  const actions = useMemo(
    () => ({
      setData: (data: unknown[]) => dispatch({ type: "SET_DATA", data }),
      accumulateData: (data: unknown[]) => dispatch({ type: "ACCUMULATE_DATA", data }),
      setPage: (pageIndex: number) => dispatch({ type: "SET_PAGE", pageIndex }),
      setPageSize: (pageSize: number) =>
        dispatch({ type: "SET_PAGE_SIZE", pageSize }),
      toggleSort: (field: string) => dispatch({ type: "SET_SORT", field }),
      clearSort: () => dispatch({ type: "CLEAR_SORT" }),
      setSearch: (text: string) => dispatch({ type: "SET_SEARCH", text }),
      setFilter: (name: string, value: unknown) =>
        dispatch({ type: "SET_FILTER", name, value }),
      setFilters: (filters: Record<string, unknown>) =>
        dispatch({ type: "SET_FILTERS", filters }),
      clearFilters: () => dispatch({ type: "CLEAR_FILTERS" }),
      selectRow: (key: string) => dispatch({ type: "SELECT_ROW", key }),
      deselectRow: (key: string) => dispatch({ type: "DESELECT_ROW", key }),
      selectAll: (keys: string[]) => dispatch({ type: "SELECT_ALL", keys }),
      deselectAll: () => dispatch({ type: "DESELECT_ALL" }),
      toggleRow: (key: string) => dispatch({ type: "TOGGLE_ROW", key }),
      startEditing: (rowKey: string, initialValues: Record<string, unknown>) =>
        dispatch({ type: "START_EDITING", rowKey, initialValues }),
      startEditingBatch: (entries: Array<{ rowKey: string; initialValues: Record<string, unknown> }>) =>
        dispatch({ type: "START_EDITING_BATCH", entries }),
      updateEditValue: (rowKey: string, field: string, value: unknown) =>
        dispatch({ type: "UPDATE_EDIT_VALUE", rowKey, field, value }),
      cancelEditing: (rowKey: string) =>
        dispatch({ type: "CANCEL_EDITING", rowKey }),
      cancelEditingBatch: (rowKeys: string[]) =>
        dispatch({ type: "CANCEL_EDITING_BATCH", rowKeys }),
      cancelAllEditing: () => dispatch({ type: "CANCEL_ALL_EDITING" }),
      setEditError: (rowKey: string, field: string, error: string) =>
        dispatch({ type: "SET_EDIT_ERROR", rowKey, field, error }),
      clearEditError: (rowKey: string, field: string) =>
        dispatch({ type: "CLEAR_EDIT_ERROR", rowKey, field }),
      setRowEditErrors: (rowKey: string, errors: Record<string, string>) =>
        dispatch({ type: "SET_ROW_EDIT_ERRORS", rowKey, errors }),
      setServerErrors: (errors: Map<string, Record<string, string>>) =>
        dispatch({ type: "SET_SERVER_ERRORS", errors }),
      clearServerErrors: () => dispatch({ type: "CLEAR_SERVER_ERRORS" }),
      applyEdit: (rowKey: string) => dispatch({ type: "APPLY_EDIT", rowKey }),
      toggleExpand: (rowKey: string) =>
        dispatch({ type: "TOGGLE_EXPAND", rowKey }),
      expandAll: (keys: string[]) => dispatch({ type: "EXPAND_ALL", keys }),
      collapseAll: () => dispatch({ type: "COLLAPSE_ALL" }),
      toggleGroup: (groupKey: string) =>
        dispatch({ type: "TOGGLE_GROUP", groupKey }),
      expandAllGroups: (keys: string[]) =>
        dispatch({ type: "EXPAND_ALL_GROUPS", keys }),
      collapseAllGroups: () => dispatch({ type: "COLLAPSE_ALL_GROUPS" }),
      reorderColumn: (fromIndex: number, toIndex: number) =>
        dispatch({ type: "REORDER_COLUMN", fromIndex, toIndex }),
      setColumnOrder: (order: string[]) =>
        dispatch({ type: "SET_COLUMN_ORDER", order }),
      resetColumnOrder: () => dispatch({ type: "RESET_COLUMN_ORDER" }),
      setColumnSizes: (sizes: Record<string, number>) =>
        dispatch({ type: "SET_COLUMN_SIZES", sizes }),
      resizeColumn: (field: string, width: number) =>
        dispatch({ type: "RESIZE_COLUMN", field, width }),
      resetColumnSizes: () => dispatch({ type: "RESET_COLUMN_SIZES" }),
    }),
    [],
  );

  const computed = useMemo(
    () => ({
      hasEdits: state.editingRows.size > 0,
      hasErrors: state.editErrors.size > 0,
      hasServerErrors: state.serverErrors.size > 0,
      selectedCount: state.selectedKeys.length,
      isDirty: state.data !== state.originalData,
      editedRowKeys: Array.from(state.editingRows.keys()),
      getEditedValues: () => {
        const result: unknown[] = [];
        state.editingRows.forEach((values, key) => {
          const original = state.data.find((r) => {
            const rec = r as Record<string, unknown>;
            return String(rec.id ?? "") === key;
          });
          if (original) {
            const origRec = original as Record<string, unknown>;
            result.push({ ...origRec, ...values });
          }
        });
        return result;
      },
    }),
    [state],
  );

  return { state, actions, computed };
}
